/**
 * Data hook for the Planning View (A2F-444, A2F-456).
 *
 * Fetches all submissions + per-submission classification, Review scans,
 * section owners, and explorer docs. Computes per-submission metrics
 * (Signed Off, Consistency) using the document → submission mapping.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import { buildDocSubmissionMap } from '@/lib/findingClassification';
import {
  computeSubmissionMetrics,
  getDocIdsForSubmission,
  type DocOwnersBySubmissionDoc,
  type SubmissionMetrics,
} from '@/lib/submissionMetrics';
import { onConsistencyInvalidated } from '@/lib/consistencyInvalidation';
import { onDocumentReadinessInvalidated } from '@/lib/documentReadinessInvalidation';
import type {
  Submission,
  SubmissionClassification,
  SectionOwnerScanResponse,
} from '@/types/api';

export interface PlanningData {
  submissions: Submission[];
  classifications: Map<string, SubmissionClassification>;
  sectionOwners: SectionOwnerScanResponse | null;
  /** Per-submission Signed Off + Consistency metrics (A2F-456). */
  metrics: Map<string, SubmissionMetrics>;
  loading: boolean;
  refresh: () => void;
}

export function usePlanningData(): PlanningData {
  const { activeProject } = useProject();
  const projectId = activeProject?.id ?? '';

  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [classifications, setClassifications] = useState<Map<string, SubmissionClassification>>(new Map());
  const [sectionOwners, setSectionOwners] = useState<SectionOwnerScanResponse | null>(null);
  const [metrics, setMetrics] = useState<Map<string, SubmissionMetrics>>(new Map());
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);

    try {
      // Phase 1: project-wide — submissions, section owners, explorer docs.
      // A2F-462 follow-up: the 5 per-feature GET calls are gone; per-submission
      // consistency is fetched via the aggregate endpoint in phase 3 below.
      const [subsR, soR, explorerR] = await Promise.allSettled([
        api.projects.submissions(projectId, true),
        api.sectionOwners.get(projectId),
        api.documents.explorer(projectId),
      ]);

      const subsList = subsR.status === 'fulfilled' ? subsR.value : [];
      const so = soR.status === 'fulfilled' ? soR.value : null;
      const explorerDocs = explorerR.status === 'fulfilled' ? explorerR.value.documents : [];

      setSubmissions(subsList);
      setSectionOwners(so);

      const docSubMap = buildDocSubmissionMap(explorerDocs);

      // Phase 2: per-submission classification.
      const planningSubmissions = subsList.filter(s => s.isPlanning);
      const classPromises = planningSubmissions
        .filter(s => s.submissionTypeCode)
        .map(async (s) => {
          try {
            const cls = await api.submissionTypes.getClassification(projectId, s.id);
            return [s.id, cls] as const;
          } catch {
            return null;
          }
        });
      const classResults = await Promise.allSettled(classPromises);
      const classMap = new Map<string, SubmissionClassification>();
      for (const r of classResults) {
        if (r.status === 'fulfilled' && r.value) {
          classMap.set(r.value[0], r.value[1]);
        }
      }
      setClassifications(classMap);

      // Phase 3: per-submission consistency aggregate (summary mode — Plan
      // only needs the aggregate, not full per_feature findings).
      const consPromises = planningSubmissions.map(async (s) => {
        try {
          const r = await api.consistency.get(projectId, s.id, true);
          return [s.id, r.aggregate] as const;
        } catch {
          return [s.id, null] as const;
        }
      });
      const consResults = await Promise.allSettled(consPromises);
      const consPerSub = new Map<string, ReturnType<typeof computeSubmissionMetrics> extends SubmissionMetrics ? Parameters<typeof computeSubmissionMetrics>[2] : never>();
      for (const r of consResults) {
        if (r.status === 'fulfilled') {
          consPerSub.set(r.value[0], r.value[1]);
        }
      }

      // Phase 4: per-submission section owners (same pattern as before).
      const soPerSubPromises = planningSubmissions.map(async (s) => {
        try {
          const subSo = await api.sectionOwners.get(projectId, s.id);
          return [s.id, subSo] as const;
        } catch {
          return [s.id, null] as const;
        }
      });
      const soPerSubResults = await Promise.allSettled(soPerSubPromises);
      const soPerSub = new Map<string, SectionOwnerScanResponse | null>();
      for (const r of soPerSubResults) {
        if (r.status === 'fulfilled') {
          soPerSub.set(r.value[0], r.value[1]);
        }
      }

      // Phase 5: per-submission document_owner_assignments (A2F-666). One
      // request per planning submission — typical projects have <10 active
      // subs so the round-trip cost is negligible. The shape from the
      // backend is `{ assignments: { [docId]: OwnerEntry[] } }`.
      const doPerSubPromises = planningSubmissions.map(async (s) => {
        try {
          const r = await api.documentReadiness.getAssignments(s.id);
          return [s.id, r.assignments as DocOwnersBySubmissionDoc] as const;
        } catch {
          return [s.id, null] as const;
        }
      });
      const doPerSubResults = await Promise.allSettled(doPerSubPromises);
      const doPerSub = new Map<string, DocOwnersBySubmissionDoc | null>();
      for (const r of doPerSubResults) {
        if (r.status === 'fulfilled') {
          doPerSub.set(r.value[0], r.value[1]);
        }
      }

      // Compose metrics: % Content Done from local section-owner logic,
      // % Document Done from local document_owner_assignments, consistency
      // from the server aggregate.
      const metricsMap = new Map<string, SubmissionMetrics>();
      for (const sub of planningSubmissions) {
        const subDocIds = getDocIdsForSubmission(docSubMap, sub.id);
        const subSo = soPerSub.get(sub.id) ?? so;
        const subCons = consPerSub.get(sub.id) ?? null;
        const subDo = doPerSub.get(sub.id) ?? null;
        metricsMap.set(sub.id, computeSubmissionMetrics(subDocIds, subSo, subCons, subDo));
      }
      setMetrics(metricsMap);
    } catch {
      // silently ignore
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // A2F-462 follow-up: refetch when any scan completes anywhere in the app.
  const projectIdRef = useRef(projectId);
  projectIdRef.current = projectId;
  useEffect(() => {
    return onConsistencyInvalidated((invalidatedProjectId) => {
      if (invalidatedProjectId === projectIdRef.current) {
        fetchData();
      }
    });
  }, [fetchData]);

  // A2F-758: refetch when a document sign-off or owner change completes.
  // Separate from the Consistency channel — a sign-off is not a Review
  // consistency change. fetchData() re-runs all phases (1–5), so this
  // single trigger refreshes both % Document Done (Phase 5, getAssignments)
  // and % Content Done (Phase 4, sectionOwners) in one pass.
  useEffect(() => {
    return onDocumentReadinessInvalidated((invalidatedProjectId) => {
      if (invalidatedProjectId === projectIdRef.current) {
        fetchData();
      }
    });
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    submissions,
    classifications,
    sectionOwners,
    metrics,
    loading,
    refresh: fetchData,
  };
}
